(() => {
    if (window.productPageInitialized) return;
    window.productPageInitialized = true;

    const syncProductTitleToggle = (title) => {
        if (!(title instanceof HTMLElement)) return;

        const expanded = title.dataset.titleExpanded === 'true';
        const lineHeight = Number.parseFloat(getComputedStyle(title).lineHeight);
        const collapsedHeight = Number.isFinite(lineHeight) ? lineHeight * 2 : title.clientHeight;
        const isOverflowing = title.scrollHeight > collapsedHeight + 1;

        if (!isOverflowing) {
            title.removeAttribute('data-title-toggle-enabled');
            title.removeAttribute('data-title-expanded');
            title.removeAttribute('aria-expanded');
            title.removeAttribute('tabindex');
            title.style.removeProperty('--product-title-full-height');
            return;
        }

        title.dataset.titleToggleEnabled = 'true';
        title.dataset.titleExpanded = String(expanded);
        title.setAttribute('aria-expanded', String(expanded));
        title.tabIndex = 0;
        title.style.setProperty('--product-title-full-height', `${title.scrollHeight + 1}px`);
    };

    const syncProductTitleToggles = () => {
        document.querySelectorAll('.product-page .product__title h1[data-product-title]').forEach(syncProductTitleToggle);
    };

    const toggleProductTitle = (title) => {
        if (title.dataset.titleToggleEnabled !== 'true') return;

        const expanded = title.dataset.titleExpanded !== 'true';
        title.dataset.titleExpanded = String(expanded);
        title.setAttribute('aria-expanded', String(expanded));
        title.style.setProperty('--product-title-full-height', `${title.scrollHeight + 1}px`);
    };

    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const title = event.target.closest('.product-page .product__title h1[data-product-title]');
        if (title?.dataset.titleToggleEnabled === 'true') toggleProductTitle(title);
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        const title = event.target instanceof Element
            ? event.target.closest('.product-page .product__title h1[data-product-title]')
            : null;
        if (title?.dataset.titleToggleEnabled !== 'true') return;

        event.preventDefault();
        toggleProductTitle(title);
    });

    window.addEventListener('resize', syncProductTitleToggles);
    document.addEventListener('product-info:loaded', syncProductTitleToggles);
    document.addEventListener('shopify:section:load', syncProductTitleToggles);
    syncProductTitleToggles();

    if (document.fonts?.ready) document.fonts.ready.then(syncProductTitleToggles);

    const getProductInfoAccordionItems = (accordion) => Array.from(
        accordion.querySelectorAll('[data-product-info-accordion-trigger]')
    ).map((trigger) => trigger.closest('.product-info-accordion__item')).filter(Boolean);

    const setProductInfoAccordionState = (item, expanded) => {
        const trigger = item?.querySelector('[data-product-info-accordion-trigger]');
        const panel = item?.querySelector('[data-product-info-accordion-panel]');
        if (!trigger || !panel) return;

        item.classList.toggle('is-open', expanded);
        trigger.setAttribute('aria-expanded', String(expanded));
        panel.setAttribute('aria-hidden', String(!expanded));
        panel.hidden = !expanded;
    };

    const activateProductInfoAccordion = (accordion, selectedItem, allowCollapse = false) => {
        const selectedTrigger = selectedItem?.querySelector('[data-product-info-accordion-trigger]');
        const shouldExpand = selectedItem && (
            !allowCollapse || selectedTrigger?.getAttribute('aria-expanded') !== 'true'
        );

        getProductInfoAccordionItems(accordion).forEach((item) => {
            setProductInfoAccordionState(item, item === selectedItem && shouldExpand);
        });
    };

    const initializeProductInfoAccordion = (accordion) => {
        const items = getProductInfoAccordionItems(accordion);
        const selectedItem = items.find((item) => (
            item.querySelector('[data-product-info-accordion-trigger]')?.getAttribute('aria-expanded') === 'true'
        )) || items[0];

        if (selectedItem) activateProductInfoAccordion(accordion, selectedItem);
    };

    const initializeAllProductInfoAccordions = () => {
        document.querySelectorAll('.product-page [data-product-info-accordion]').forEach(initializeProductInfoAccordion);
    };

    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const trigger = event.target.closest('[data-product-info-accordion-trigger]');
        const accordion = trigger?.closest('.product-page [data-product-info-accordion]');
        if (!trigger || !accordion) return;

        const item = trigger.closest('.product-info-accordion__item');
        if (item) activateProductInfoAccordion(accordion, item, true);
    });

    initializeAllProductInfoAccordions();
    document.addEventListener('product-info:loaded', initializeAllProductInfoAccordions);
    document.addEventListener('shopify:section:load', initializeAllProductInfoAccordions);

    const customVariantDropdownStates = new WeakMap();
    const observedProductPages = new WeakSet();

    const closeCustomVariantDropdown = (state, returnFocus = false) => {
        state.wrapper.classList.remove('is-open');
        state.trigger.setAttribute('aria-expanded', 'false');
        state.menu.hidden = true;
        if (returnFocus) state.trigger.focus();
    };

    const closeOtherCustomVariantDropdowns = (currentState) => {
        document.querySelectorAll('.product-page [data-product-variant-select].is-open').forEach((wrapper) => {
            const state = customVariantDropdownStates.get(wrapper);
            if (state && state !== currentState) closeCustomVariantDropdown(state);
        });
    };

    const initializeCustomVariantDropdown = (wrapper) => {
        let state = customVariantDropdownStates.get(wrapper);
        const select = wrapper.querySelector('select.select__select');
        if (!select) return;

        if (!state) {
            const trigger = document.createElement('button');
            const menu = document.createElement('div');
            const triggerId = `${select.id}-custom-trigger`;
            const menuId = `${select.id}-custom-options`;

            trigger.type = 'button';
            trigger.id = triggerId;
            trigger.className = 'product-page__custom-select-trigger';
            trigger.setAttribute('aria-haspopup', 'listbox');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.setAttribute('aria-controls', menuId);

            menu.id = menuId;
            menu.className = 'product-page__custom-select-menu';
            menu.setAttribute('role', 'listbox');
            menu.hidden = true;

            const nativeArrow = wrapper.querySelector(':scope > .svg-wrapper');
            if (nativeArrow) trigger.append(nativeArrow);

            const customControl = document.createElement('div');
            customControl.className = 'product-page__custom-select';
            customControl.append(trigger, menu);
            wrapper.append(customControl);

            state = { wrapper, select, trigger, menu, customControl, sync: null, rebuild: null };
            customVariantDropdownStates.set(wrapper, state);

            const getLabel = () => {
                const field = wrapper.closest('.product-form__input');
                return Array.from(field?.querySelectorAll('label') || []).find(
                    (label) => label.htmlFor === select.id
                );
            };

            const appendOptionPrice = (element, nativeOption) => {
                const optionPrice = nativeOption?.dataset.optionPrice;
                if (!optionPrice) return;

                const price = document.createElement('span');
                price.className = 'product-page__custom-select-option-price';
                price.textContent = ` (${optionPrice})`;
                element.append(price);
            };

            const focusOption = (index) => {
                const options = Array.from(menu.querySelectorAll('[role="option"]'));
                if (!options.length) return;
                const nextIndex = Math.max(0, Math.min(index, options.length - 1));
                options.forEach((option, optionIndex) => option.setAttribute('tabindex', optionIndex === nextIndex ? '0' : '-1'));
                options[nextIndex].focus();
            };

            const selectOption = (index) => {
                const nativeOption = select.options[index];
                if (!nativeOption) return;

                select.selectedIndex = index;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                closeCustomVariantDropdown(state);
                trigger.focus();
            };

            state.sync = () => {
                const selectedOption = select.options[select.selectedIndex];
                const selectedText = selectedOption?.textContent.trim() || '';
                const selectedPrice = selectedOption?.dataset.optionPrice;
                const selectedDisplayText = selectedPrice ? `${selectedText} (${selectedPrice})` : selectedText;
                const label = getLabel();
                trigger.setAttribute(
                    'aria-label',
                    label ? `${label.textContent.trim()}: ${selectedDisplayText}` : selectedDisplayText
                );

                const value = trigger.querySelector('[data-custom-select-value]');
                if (value) {
                    value.replaceChildren(document.createTextNode(selectedText));
                    if (selectedPrice) {
                        const price = document.createElement('span');
                        price.className = 'product-page__custom-select-price';
                        price.textContent = ` (${selectedPrice})`;
                        value.append(price);
                    }
                }

                menu.querySelectorAll('[role="option"]').forEach((option, index) => {
                    option.setAttribute('aria-selected', String(index === select.selectedIndex));
                });
            };

            state.rebuild = () => {
                const value = document.createElement('span');
                value.dataset.customSelectValue = '';

                const existingValue = trigger.querySelector('[data-custom-select-value]');
                if (existingValue) existingValue.replaceWith(value);
                else trigger.prepend(value);

                menu.replaceChildren();
                Array.from(select.options).forEach((nativeOption, index) => {
                    const option = document.createElement('button');
                    const unavailable = nativeOption.dataset.optionAvailable === 'false' || nativeOption.disabled;

                    option.type = 'button';
                    option.className = 'product-page__custom-select-option';
                    option.setAttribute('role', 'option');
                    option.setAttribute('tabindex', index === select.selectedIndex ? '0' : '-1');
                    option.setAttribute('aria-selected', String(index === select.selectedIndex));
                    option.setAttribute('aria-disabled', String(unavailable));
                    option.dataset.value = nativeOption.value;
                    if (nativeOption.dataset.optionValueId) option.dataset.optionValueId = nativeOption.dataset.optionValueId;
                    if (nativeOption.dataset.productUrl) option.dataset.productUrl = nativeOption.dataset.productUrl;
                    if (unavailable) option.classList.add('is-unavailable');
                    option.textContent = nativeOption.textContent.trim();
                    appendOptionPrice(option, nativeOption);

                    option.addEventListener('click', () => selectOption(index));
                    option.addEventListener('keydown', (event) => {
                        const options = Array.from(menu.querySelectorAll('[role="option"]'));
                        const currentIndex = options.indexOf(event.currentTarget);

                        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                            event.preventDefault();
                            focusOption(currentIndex + (event.key === 'ArrowDown' ? 1 : -1));
                        } else if (event.key === 'Home' || event.key === 'End') {
                            event.preventDefault();
                            focusOption(event.key === 'Home' ? 0 : options.length - 1);
                        } else if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            selectOption(currentIndex);
                        } else if (event.key === 'Escape') {
                            event.preventDefault();
                            closeCustomVariantDropdown(state, true);
                        } else if (event.key === 'Tab') {
                            closeCustomVariantDropdown(state);
                        }
                    });
                    menu.append(option);
                });

                state.sync();
            };

            trigger.addEventListener('click', () => {
                const isOpen = wrapper.classList.contains('is-open');
                if (isOpen) {
                    closeCustomVariantDropdown(state);
                    return;
                }

                closeOtherCustomVariantDropdowns(state);
                wrapper.classList.add('is-open');
                trigger.setAttribute('aria-expanded', 'true');
                menu.hidden = false;
                focusOption(select.selectedIndex);
            });

            trigger.addEventListener('keydown', (event) => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (!wrapper.classList.contains('is-open')) trigger.click();
                    else focusOption(select.selectedIndex + (event.key === 'ArrowUp' ? -1 : 0));
                } else if (event.key === 'Escape') {
                    closeCustomVariantDropdown(state);
                }
            });

            select.addEventListener('change', state.sync);
            select.classList.add('product-page__native-variant-select');
            select.tabIndex = -1;
            select.setAttribute('aria-hidden', 'true');
            wrapper.classList.add('product-page__custom-select-ready');

            const selectObserver = new MutationObserver(() => state.rebuild());
            selectObserver.observe(select, { childList: true, subtree: true, attributes: true });
            state.selectObserver = selectObserver;
        }

        state.rebuild();
    };

    const initializeCustomVariantDropdowns = () => {
        document.querySelectorAll('.product-page [data-product-variant-select]').forEach(initializeCustomVariantDropdown);

        document.querySelectorAll('.product-page').forEach((productPage) => {
            if (observedProductPages.has(productPage)) return;
            observedProductPages.add(productPage);

            const observer = new MutationObserver((mutations) => {
                const hasVariantMarkupChange = mutations.some((mutation) =>
                    Array.from(mutation.addedNodes).some(
                        (node) =>
                            node.nodeType === 1 &&
                            (node.matches('variant-selects, [data-product-variant-select]') ||
                                node.querySelector('variant-selects, [data-product-variant-select]'))
                    )
                );
                if (hasVariantMarkupChange) initializeCustomVariantDropdowns();
            });
            observer.observe(productPage, { childList: true, subtree: true });
        });
    };

    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || event.target.closest('[data-product-variant-select]')) return;
        document.querySelectorAll('.product-page [data-product-variant-select].is-open').forEach((wrapper) => {
            const state = customVariantDropdownStates.get(wrapper);
            if (state) closeCustomVariantDropdown(state);
        });
    });

    initializeCustomVariantDropdowns();
    document.addEventListener('shopify:section:load', initializeCustomVariantDropdowns);

    const initializeGalleryDots = () => {
        document.querySelectorAll('.product-page media-gallery').forEach((gallery) => {
            if (gallery.dataset.productGalleryDotsInitialized === 'true') return;

            const viewer = gallery.querySelector("slider-component[id^='GalleryViewer-']");
            const mediaList = viewer?.querySelector('.product__media-list');
            if (!viewer || !mediaList) return;

            gallery.dataset.productGalleryDotsInitialized = 'true';

            const dots = document.createElement('div');
            dots.className = 'product-gallery__dots';
            dots.setAttribute('aria-hidden', 'true');
            viewer.insertAdjacentElement('afterend', dots);

            const getSlides = () =>
                Array.from(mediaList.children).filter((slide) => slide.matches('[data-media-id]'));

            const syncDots = (activeSlide) => {
                const slides = getSlides();
                const mediaIds = slides.map((slide) => slide.dataset.mediaId);
                const dotIds = Array.from(dots.children).map((dot) => dot.dataset.mediaId);

                if (mediaIds.join('|') !== dotIds.join('|')) {
                    dots.replaceChildren(
                        ...mediaIds.map((mediaId) => {
                            const dot = document.createElement('span');
                            dot.className = 'product-gallery__dot';
                            dot.dataset.mediaId = mediaId;
                            return dot;
                        })
                    );
                }

                const activeMediaId =
                    activeSlide?.dataset.mediaId ||
                    mediaList.querySelector('.product__media-item.is-active')?.dataset.mediaId ||
                    mediaIds[0];

                Array.from(dots.children).forEach((dot) => {
                    dot.classList.toggle('is-active', dot.dataset.mediaId === activeMediaId);
                });
                gallery.classList.toggle('product-gallery--multiple-media', mediaIds.length > 1);
                dots.hidden = mediaIds.length < 2;
            };

            viewer.addEventListener('slideChanged', (event) => syncDots(event.detail.currentElement));
            new MutationObserver(() => syncDots()).observe(mediaList, { childList: true });
            syncDots();
        });
    };

    initializeGalleryDots();
    document.addEventListener('shopify:section:load', initializeGalleryDots);
})();
